import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { usePageTitle } from "hooks/usePageTitle";
import { useHistory, useParams } from "react-router-dom";
import {
  useCompleteReconciliationMutation,
  useGetReconciliationTxnsQuery,
} from "store/apis/reconciliation";
import { ReconciliationConsole } from "./ReconciliationConsole";
import { ReconciliationTable } from "./ReconciliationTable";
import { useQuery } from "hooks/useQuery";
import Modal from "components/DesignSystem/Modal/Modal";
import { QuestionWithCircle } from "components/icons/QuestionWithCircle";
import { TRANSACTION_COLUMNS } from "constants/transaction";
import { useState, useMemo } from "react";
import Popover from "components/DesignSystem/Popover/Popover";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { AIReconcileDetailModal } from "components/ChatCard/AIReconcileCard/AIReconcileDetailModal";

export const ReconciliationHeader = ({
  brandName,
  accountName,
}: {
  brandName: string;
  accountName: string;
}) => {
  return (
    <>
      <div>
        <Modal.Title>Reconcile</Modal.Title>
        <Modal.Subtitle>
          {brandName} {accountName}
        </Modal.Subtitle>
      </div>

      <Modal.Close type="button" />
    </>
  );
};

export const ReconciliationTxn = () => {
  usePageTitle("Reconciliation");
  const [cardVisible, setCardVisible] = useState<boolean>(false);
  const [unReconciledTxnIds, setUnReconciledTxnIds] = useState<string[]>([]);
  const [transactionIdsToReconcile, setTransactionIdsToReconcile] = useState<
    string[]
  >([]);
  const history = useHistory();
  const query = useQuery();
  const { reconcilationId } = useParams<{ reconcilationId: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: string;
    }[]
  >([
    {
      id: TRANSACTION_COLUMNS.DATE,
      desc: true,
    },
  ]);
  const { alertToast, successToast } = useToast();
  const [completeReconciliation, { isLoading: confirmLoading }] =
    useCompleteReconciliationMutation();

  const fromDate = query.get("START_DATE");
  const toDate = query.get("END_DATE");

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc;

  const handleClose = () => {
    history.go(-2);
  };

  const {
    data: csvTxn,
    isLoading,
    isSuccess,
  } = useGetReconciliationTxnsQuery(
    {
      reconcilationId,
      entityId,
      from_date: fromDate!,
      to_date: toDate!,
      sort_col: sortCol,
      sort_order: sortOrder ? ("DSC" as const) : ("ASC" as const),
    },
    { skip: !reconcilationId || !entityId }
  );

  const {
    as_of_start_date_balance = 0,
    inkle_total_balance = 0,
    statement_ending_balance = 0,
    money_in = 0,
    money_out = 0,
    entity_bank_account_id,
    transactions = [],
    reconciliation_end_date,
  } = csvTxn || {};

  const { data: ledger } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const { accounts = [] } = ledger || {};

  const reconcileBank = accounts.find(
    ({ account: { uuid } }) => uuid === entity_bank_account_id
  );

  const reconcileAccount = reconcileBank?.account;

  const onComplete = async () => {
    try {
      await completeReconciliation({
        entityId,
        groupId,
        reconcilationId,
        unreconciled_transaction_ids: unReconciledTxnIds,
        txn_ids_to_reconcile: transactionIdsToReconcile,
      }).unwrap();

      const bankName = reconcileAccount?.nickname;
      const endDate = dayjs(reconciliation_end_date).format(DISPLAY_DATE);

      successToast({
        title: "Reconciliation completed successfully",
        message: `For ${bankName} till ${endDate}`,
      });

      handleClose();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const { adjustedMoneyIn, adjustedMoneyOut } = useMemo(() => {
    let adjustedIn = money_in || 0;
    let adjustedOut = money_out || 0;

    unReconciledTxnIds.forEach((txnId) => {
      const txn = transactions.find((t) => t.transaction.uuid === txnId);
      if (txn) {
        const amount = txn.transaction.amount || 0;
        if (amount < 0) {
          adjustedOut = adjustedOut - amount;
        } else {
          adjustedIn = adjustedIn - amount;
        }
      }
    });

    transactionIdsToReconcile.forEach((txnId) => {
      const txn = transactions.find((t) => t.transaction.uuid === txnId);
      if (txn) {
        const amount = txn.transaction.amount || 0;
        if (amount < 0) {
          adjustedOut = adjustedOut + amount;
        } else {
          adjustedIn = adjustedIn + amount;
        }
      }
    });

    return {
      adjustedMoneyIn: parseFloat(adjustedIn.toFixed(2)),
      adjustedMoneyOut: parseFloat(adjustedOut.toFixed(2)),
    };
  }, [
    unReconciledTxnIds,
    transactionIdsToReconcile,
    transactions,
    money_in,
    money_out,
  ]);

  const inkleBooksBalance = parseFloat(
    (
      (as_of_start_date_balance || 0) +
      adjustedMoneyIn +
      adjustedMoneyOut
    ).toFixed(2)
  );
  const difference = parseFloat(
    ((statement_ending_balance || 0) - inkleBooksBalance).toFixed(2)
  );

  const differenceZero = Boolean(difference === 0);

  if (isLoading || !isSuccess) {
    return (
      <Modal.Root open onOpenChange={handleClose}>
        <Modal.Content size="fullscreen" useCustomOverlay>
          <Modal.Header className="t-mx-8 !t-px-0">
            <ReconciliationHeader
              brandName={reconcileBank?.bank_brand.name ?? ""}
              accountName={reconcileAccount?.nickname ?? ""}
            />
          </Modal.Header>
          <Modal.Body className="t-flex-1">
            <Loader />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  return (
    <Modal.Root open onOpenChange={handleClose}>
      <Modal.Content size="fullscreen" useCustomOverlay>
        <Modal.Header className="t-mx-8 !t-px-0">
          <ReconciliationHeader
            brandName={reconcileBank?.bank_brand.name ?? ""}
            accountName={reconcileAccount?.nickname ?? ""}
          />
        </Modal.Header>
        <Modal.Body className="t-flex-1">
          <div className="t-pt-0 t-flex t-flex-col t-gap-5">
            <div className="t-flex t-gap-6 t-flex-col">
              <ReconciliationConsole
                reconcilTxn={csvTxn}
                inkleBooksBalance={inkleBooksBalance}
                difference={difference}
                adjustedMoneyIn={adjustedMoneyIn}
                adjustedMoneyOut={adjustedMoneyOut}
              />
              <ReconciliationTable
                reconcilTxn={csvTxn}
                sorting={sorting}
                setSorting={setSorting}
                unReconciledTxnIds={unReconciledTxnIds}
                transactionIdsToReconcile={transactionIdsToReconcile}
                setUnReconciledTxnIds={setUnReconciledTxnIds}
                setTransactionIdsToReconcile={setTransactionIdsToReconcile}
                reconcileAccount={reconcileAccount}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-w-full t-justify-between t-items-center sm:t-gap-2">
            <Popover.Root open={cardVisible} onOpenChange={setCardVisible}>
              <Popover.Trigger asChild>
                <Button
                  size="small"
                  customType="ghost"
                  aria-label="See how it works"
                >
                  <div className="t-flex t-gap-1 t-items-center">
                    <span className="t-text-text-100 sm:t-hidden">
                      See how it works
                    </span>
                    <span className="t-hidden t-text-text-60 sm:t-inline">
                      Help
                    </span>
                    <QuestionWithCircle />
                  </div>
                </Button>
              </Popover.Trigger>

              <Popover.Portal>
                <Popover.Content
                  side="top"
                  align="end"
                  className=" t-z-popover t-w-96 sm:t-w-72 t-shadow-light-60 t-bg-surface t-rounded-lg t-p-6 t-pt-5"
                >
                  <Popover.Arrow className="t-fill-white" />
                  <div className=" t-flex t-flex-col !t-text-start t-gap-3 t-text-body-sm t-text-text-30">
                    <div>
                      During reconciliation, Inkle Books shows all transactions
                      for the selected period.
                    </div>
                    <div>
                      <div className="t-mb-2">
                        You can adjust them as needed:
                      </div>
                      <ul className="t-list-disc t-pl-5 t-flex t-flex-col t-gap-1">
                        <li>
                          <span className="t-text-subtext-sm t-text-text-100">
                            Toggle off
                          </span>{" "}
                          to keep a transaction out of this reconciliation (it
                          remains in your ledger).
                        </li>
                        <li>
                          <span className="t-text-subtext-sm t-text-text-100">
                            Add manually
                          </span>{" "}
                          if a statement transaction is missing in Inkle.
                        </li>
                        <li>
                          <span className="t-text-subtext-sm t-text-text-100">
                            Exclude
                          </span>{" "}
                          if a transaction is extra or duplicated (removes it
                          from your ledger).
                        </li>
                      </ul>
                    </div>
                    <div>
                      Once balances match, confirm the reconciliation to mark it
                      completed.
                    </div>
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>

            <div className="t-flex t-gap-3">
              <Button customType="secondary" onClick={handleClose}>
                Cancel
              </Button>

              <Button
                customType="primary"
                onClick={onComplete}
                disabled={!differenceZero || confirmLoading}
                isLoading={confirmLoading}
              >
                Confirm reconciliation
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
